import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import { alpha, styled } from "@mui/material/styles";
import { setSearchActive, setSearchText } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import { isUndefined } from "lodash";
import React, { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { minSearchStrLength } from "utils/CommonConstants";
import { planTypes } from "utils/Types";
import Typography from "../Typography";
import CrossIcon from "./CrossIcon";
import "./index.scss";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "row",
  borderRadius: "8px",
  height: "100%",
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
    boxShadow: "0px 0px 4px rgba(19, 41, 138, 0.1), 0px 10px 25px rgba(19, 41, 138, 0.1)"
  },

  "&:focus": {
    backgroundColor: tokens.Background.General.Primary,
    border: `2px solid ${tokens.Border.General.LightColor}`
  },
  "&:active": {
    backgroundColor: tokens.Background.General.Primary
  },
  "&:disabled": {
    backgroundColor: tokens.Background.General.Secondary
  }
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center"
}));

interface Props {
  divider?: boolean;
  getInputDetails?: (prop: string) => void;
  value?: string;
  placeholder?: string;
  planType?: string;
  sx?: {};
  className?: string;
  isPreDefinedNav?: boolean;
  showRecentSearch?: boolean;
  recentSearchHistory?: any;
  pageType?: string;
  recentSearchWidth?: string;
  libraryRecentSearch?: any;
  setRecentSearch?: any;
}

export default function SearchAppBar(props: Props) {
  const {
    getInputDetails,
    value = "",
    placeholder = "Search by name",
    planType = "",
    sx,
    className = "",
    isPreDefinedNav = false,
    libraryRecentSearch,
    setRecentSearch
  } = props;
  const [inputText, setInputText] = React.useState(value);
  const [isFocused, setFocuedOut] = React.useState<boolean>(false);
  const { isMobileScreen } = useContext(ThemeContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const prevLocation = useRef(location);
  const searchParams = new URLSearchParams(location.search);

  const onChange = (event: any) => {
    if (planType === "filter") getInputDetails?.(event.target.value);
    setInputText(event.target.value);
  };

  function searchItems(query: string, list: any) {
    if (!query && query.length < 3) return list; // if search box empty, return all
    const lowerQuery = query.toLowerCase();
    return list.filter((item: any) => item.toLowerCase().includes(lowerQuery));
  }

  const persistRecentSearch = (result: any) => {
    const updatedSearches = [result, ...(libraryRecentSearch || [])];
    dispatch(setRecentSearch(updatedSearches));
  };

  useEffect(() => {
    setInputText(value);
  }, [value]);

  const handleSubmit = (query?: string) => {
    const newParam = isUndefined(query) ? inputText : query;
    const withoutLeadingSpace = newParam.replace(/^\s+/, "");
    if (withoutLeadingSpace.length > minSearchStrLength) {
      if (newParam && planType === planTypes.AllPlans) dispatch(setSearchActive(true));
      if (!newParam && planType === planTypes.AllPlans) dispatch(setSearchActive(false));
      getInputDetails?.(newParam);
      dispatch(setSearchText(newParam));
      if (!isUndefined(setRecentSearch)) persistRecentSearch(newParam);

      if (!isPreDefinedNav) {
        searchParams.set("search", newParam);
        navigate(`${location.pathname}?${searchParams.toString()}`, {
          replace: location.pathname === prevLocation.current.pathname
        });
      }
    }
  };

  return (
    <Box
      width={isMobileScreen ? "100%" : "auto"}
      sx={{
        borderRadius: "5px",
        height: "50px",
        position: "relative"
      }}
      id="SearchBar"
      className={className}
    >
      <Search
        sx={{
          backgroundColor: tokens.Background.General.Primary,
          paddingRight: "12px",
          ...sx
        }}
      >
        <SearchIconWrapper>
          <SearchIcon />
        </SearchIconWrapper>
        <InputBase
          onFocus={() => setFocuedOut(true)}
          onBlur={() => setFocuedOut(false)}
          sx={{ width: "100%", paddingLeft: "48px", fontWeight: "600" }}
          endAdornment={
            inputText ? (
              <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <CrossIcon
                  sx={{ fontSize: "20px" }}
                  onClick={() => {
                    setInputText("");
                    dispatch(setSearchActive(false));
                    getInputDetails?.("");
                    dispatch(setSearchText(""));
                    if (!isPreDefinedNav) {
                      searchParams.set("search", "");
                      navigate(`${location.pathname}?${searchParams.toString()}`, {
                        replace: location.pathname === prevLocation.current.pathname
                      });
                    }
                  }}
                />
              </Box>
            ) : (
              ""
            )
          }
          onChange={onChange}
          placeholder={placeholder}
          value={inputText}
          inputProps={{
            "aria-label": "search",
            onKeyPress: (event) => {
              if (event.key === "Enter") {
                handleSubmit();
                event.preventDefault();
              }
            }
          }}
        />
      </Search>
      {isFocused && Array.isArray(libraryRecentSearch) && (
        <div
          className="library-recent-search-wrapper"
          style={{
            width: props.recentSearchWidth || "100%"
          }}
        >
          {searchItems(inputText, libraryRecentSearch)?.map((data: any) => {
            return (
              <div
                className="cursor-pointer recent-search-list"
                style={{ minHeight: "32px", display: "flex", padding: "16px", alignItems: "center" }}
                onMouseDown={(e: React.MouseEvent) => {
                  e.preventDefault();
                  handleSubmit(data);
                }}
              >
                <Typography sx={{ marginX: "8px" }} variant={"body2"}>
                  {data}
                </Typography>
              </div>
            );
          })}
        </div>
      )}
    </Box>
  );
}
